import {
  Card,
  Table,
  Paper,
  TableRow,
  TableCell,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import Scrollbar from "../scrollbar";
import { formatDate } from "src/utils/feedutils";
import FixtureFeedsTableBody from "./FixtureFeedsTableBody";

export default function FixtureFeedsTable({
  loading,
  feeds,
  totalCount,
  onLoadMore,
  hasMore,
}) {
  const [showFeedDetails, setShowFeedDetails] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [feedContent, setFeedContent] = useState(null);
  const [isLoadingFeedContent, setIsLoadingFeedContent] = useState(false);

  const handleSearch = (query) => {
    if (!feedContent) return;

    const searchObject = (obj, query) => {
      let matches = [];

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          const itemMatches = searchObject(item, query);
          if (itemMatches.length > 0) {
            matches.push(item);
          }
        });
      } else if (typeof obj === "object" && obj !== null) {
        for (const [key, value] of Object.entries(obj)) {
          if (
            key.toLowerCase().includes(query.toLowerCase()) ||
            (typeof value === "string" &&
              value.toLowerCase().includes(query.toLowerCase())) ||
            (typeof value === "number" && value.toString().includes(query))
          ) {
            matches.push(obj);
            break;
          } else if (typeof value === "object") {
            matches = [...matches, ...searchObject(value, query)];
          }
        }
      }

      return matches;
    };

    const results = searchObject(feedContent, query);
    setSearchResults(results);
  };

  const handleViewFeedDetails = async (feed) => {
    setShowFeedDetails(feed);
    setSearchQuery("");
    setSearchResults([]);
    setFeedContent(null);
    setIsLoadingFeedContent(true);

    try {
      const content = await fetchFeedContent(feed.body);
      setFeedContent(content);
    } catch (error) {
      console.error("Error loading feed content:", error);
      setFeedContent(feed.body); // Fallback to original body
    } finally {
      setIsLoadingFeedContent(false);
    }
  };

  const isUrl = (str) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const fetchFeedContent = async (body) => {
    // Check if body is a URL
    if (typeof body === 'string' && isUrl(body)) {
      try {
        const response = await fetch(body);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await response.json();
        } else {
          return await response.text();
        }
      } catch (error) {
        console.error('Error fetching content from URL:', error);
        throw error;
      }
    } else {
      // Body is not a URL, try to parse as JSON
      return parseFeedBody(body);
    }
  };

  const parseFeedBody = (body) => {
    try {
      return JSON.parse(body);
    } catch (error) {
      return body;
    }
  };

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      await onLoadMore();
    } catch (error) {
      console.error("Error loading more feeds:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  useEffect(() => {
    const handleScroll = (event) => {
      const scrollContainer = event.target;
      if (!scrollContainer || loading || isLoadingMore || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      console.log("Scroll debug:", {
        distanceFromBottom,
        scrollTop,
        scrollHeight,
        clientHeight,
        hasMore,
        isLoadingMore,
        loading,
      });

      if (distanceFromBottom <= 100) {
        console.log("Triggering loadMore...");
        loadMore();
      }
    };

    const handleWindowScroll = () => {
      if (loading || isLoadingMore || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      console.log("Window scroll debug:", {
        distanceFromBottom,
        hasMore,
        isLoadingMore,
        loading,
      });

      if (distanceFromBottom <= 200) {
        console.log("Triggering loadMore from window scroll...");
        loadMore();
      }
    };

    const scrollContainer =
      document.querySelector(".MuiTableContainer-root") ||
      document.querySelector("[data-simplebar]") ||
      document.querySelector(".simplebar-content-wrapper");

    if (scrollContainer) {
      console.log("Found scroll container:", scrollContainer);
      scrollContainer.addEventListener("scroll", handleScroll);

      window.addEventListener("scroll", handleWindowScroll);

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("scroll", handleWindowScroll);
      };
    } else {
      console.warn("No scroll container found, using window scroll only");
      window.addEventListener("scroll", handleWindowScroll);
      return () => window.removeEventListener("scroll", handleWindowScroll);
    }
  }, [loading, isLoadingMore, hasMore, loadMore]);

  return (
    <Card>
      {loading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center" }}
          style={{ padding: 20 }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Scrollbar>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 800 }} aria-label="fixture feeds table">
                <TableHead>
                  <TableRow>
                    <TableCell>Feed ID</TableCell>
                    <TableCell>External ID</TableCell>
                    <TableCell>Feed Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <FixtureFeedsTableBody
                  feeds={feeds}
                  loading={loading}
                  isLoadingMore={isLoadingMore}
                  handleViewFeedDetails={handleViewFeedDetails}
                />
              </Table>
            </TableContainer>
          </Scrollbar>

          {feeds.length > 0 && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {hasMore && <span> - Scroll down to load more</span>}
                {!hasMore && <span> - All feeds loaded</span>}
              </Typography>
            </Box>
          )}
        </>
      )}

      <Dialog
        fullWidth
        maxWidth="lg"
        open={showFeedDetails !== null}
        onClose={() => setShowFeedDetails(null)}
        aria-labelledby="feed-details-dialog-title"
        aria-describedby="feed-details-dialog-description"
      >
        <DialogTitle id="feed-details-dialog-title">
          Feed Details - {showFeedDetails?.feedType} ({showFeedDetails?.status})
        </DialogTitle>
        <DialogContent>
          {showFeedDetails !== null && (
            <DialogContentText
              id="feed-details-dialog-description"
              component="div"
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>External ID:</strong> {showFeedDetails.externalId}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Created At:</strong>{" "}
                  {formatDate(showFeedDetails.createdAt)}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Feed Data:</strong>
                </Typography>
              </Box>

              <TextField
                label="Search Feed Data"
                variant="outlined"
                fullWidth
                value={searchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setSearchQuery(query);
                  handleSearch(query);
                }}
                sx={{ mb: 2 }}
                disabled={isLoadingFeedContent}
              />

              {isLoadingFeedContent ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading feed content...
                  </Typography>
                </Box>
              ) : (
                <>
                  {searchQuery && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        {searchResults.length > 0
                          ? `Found ${searchResults.length} match(es)`
                          : "No matches found"}
                      </Typography>
                      {searchResults.length > 0 && (
                        <Box
                          sx={{
                            backgroundColor: "#f5f5f5",
                            padding: 2,
                            borderRadius: 1,
                          }}
                        >
                          <pre
                            style={{ fontFamily: "monospace", fontSize: "12px" }}
                          >
                            {JSON.stringify(searchResults, null, 2)}
                          </pre>
                        </Box>
                      )}
                    </Box>
                  )}

                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Feed Content</strong>
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "#f5f5f5",
                      padding: 2,
                      borderRadius: 1,
                      maxHeight: "60vh",
                      overflow: "auto",
                    }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        fontFamily: "monospace",
                        fontSize: "12px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {feedContent && typeof feedContent === "object"
                        ? JSON.stringify(feedContent, null, 2)
                        : feedContent || "No content available"}
                    </pre>
                  </Box>
                </>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFeedDetails(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
